import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import SubtaskCard from './SubtaskCard';
import CreateSubtaskDialog from './CreateSubtaskDialog';
import SubtaskDrawer from './SubtaskDrawer';
import StatusFilterCards from './StatusFilterCards';
import { isTaskClosed } from '../utils/taskRules';

const STATUSES   = ['', 'Open', 'Ongoing', 'Blocked', 'On Hold', 'Closed'];
const PRIORITIES = ['', 'Critical', 'Major', 'Minor'];

export default function SubtaskList({ taskId, canCreate, canEdit, parentTask, currentUser, onViewParentTask }) {
  // If parent task is closed, all subtask mutations are blocked regardless of canCreate/canEdit
  const parentClosed = isTaskClosed(parentTask);
  const effectiveCanCreate = canCreate && !parentClosed;
  const effectiveCanEdit   = canEdit   && !parentClosed;
  const apiUrl = process.env.REACT_APP_API_URL;
  const token  = localStorage.getItem('jwt') || localStorage.getItem('token');

  const [subtasks, setSubtasks]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [progress, setProgress]       = useState({ total: 0, completed: 0, percent: 0 });
  const [showCreate, setShowCreate]   = useState(false);
  const [activeSubtask, setActiveSubtask] = useState(null);
  const [search, setSearch]           = useState('');
  const [statusFilter, setStatusFilter]   = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');

  const fetchSubtasks = useCallback(async () => {
    setLoading(true);
    try {
      const [subRes, progRes] = await Promise.all([
        axios.get(`${apiUrl}/tasks/${taskId}/subtasks`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${apiUrl}/tasks/${taskId}/subtasks/progress`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setSubtasks(subRes.data || []);
      setProgress(progRes.data || { total: 0, completed: 0, percent: 0 });
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [apiUrl, token, taskId]);

  useEffect(() => { fetchSubtasks(); }, [fetchSubtasks]);

  const handleCreated = () => {
    fetchSubtasks();
    setShowCreate(false);
  };

  const handleUpdated = (updated) => {
    setSubtasks(prev => prev.map(s => s.id === updated.id ? { ...s, ...updated } : s));
    if (activeSubtask?.id === updated.id) setActiveSubtask(s => ({ ...s, ...updated }));
    // Recalculate progress
    const next = subtasks.map(s => s.id === updated.id ? { ...s, ...updated } : s);
    const completed = next.filter(s => s.status === 'Closed').length;
    setProgress({ total: next.length, completed, percent: next.length ? Math.round((completed / next.length) * 100) : 0 });
  };

  const filtered = subtasks
    .filter(s => !search || s.title.toLowerCase().includes(search.toLowerCase()) ||
      (s.assignee_name || '').toLowerCase().includes(search.toLowerCase()))
    .filter(s => !statusFilter   || s.status   === statusFilter)
    .filter(s => !priorityFilter || s.priority === priorityFilter);

  const stats = [
    { label: 'Total',   value: subtasks.length,                                      color: 'bg-gray-50 text-gray-700 border-gray-200',      filter: '' },
    { label: 'Open',    value: subtasks.filter(s => s.status === 'Open').length,     color: 'bg-blue-50 text-blue-700 border-blue-100',      filter: 'Open' },
    { label: 'Ongoing', value: subtasks.filter(s => s.status === 'Ongoing').length,  color: 'bg-yellow-50 text-yellow-700 border-yellow-100', filter: 'Ongoing' },
    { label: 'Blocked', value: subtasks.filter(s => s.status === 'Blocked').length,  color: 'bg-red-50 text-red-700 border-red-100',         filter: 'Blocked' },
    { label: 'On Hold', value: subtasks.filter(s => s.status === 'On Hold').length,  color: 'bg-orange-50 text-orange-700 border-orange-100', filter: 'On Hold' },
    { label: 'Closed',  value: subtasks.filter(s => s.status === 'Closed').length,   color: 'bg-green-50 text-green-700 border-green-100',   filter: 'Closed' },
  ];

  return (
    <div className="mt-6">
      {/* ── Section Header ── */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-[#f0f7e6] flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-[#7AA33E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7l2 2 4-4" />
            </svg>
          </div>
          <div>
            <h2 className="text-base font-bold text-gray-900">Subtasks</h2>
            <p className="text-xs text-gray-400 mt-0.5">{subtasks.length} subtask{subtasks.length !== 1 ? 's' : ''} · {progress.completed} completed</p>
          </div>
        </div>
        {parentClosed ? (
          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gray-100 text-gray-400 text-xs font-semibold">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
            Parent Task Closed
          </span>
        ) : effectiveCanCreate ? (
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#7AA33E] text-white font-semibold text-sm hover:bg-[#6a9335] transition shadow-sm shadow-[#7AA33E]/20"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
            Create Subtask
          </button>
        ) : null}
      </div>

      {/* ── Progress Bar ── */}
      {subtasks.length > 0 && (
        <div className="mb-5 bg-white rounded-2xl border border-gray-200 shadow-sm px-5 py-4">
          <div className="flex items-center justify-between mb-2.5">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Overall Progress</span>
            <span className="text-sm font-bold text-[#7AA33E]">{progress.percent}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div
              className="bg-[#7AA33E] h-2 rounded-full transition-all duration-500"
              style={{ width: `${progress.percent}%` }}
            />
          </div>
          <p className="text-xs text-gray-400 mt-2">{progress.completed} of {progress.total} completed</p>
        </div>
      )}

      {/* ── Stats ── */}
      <StatusFilterCards
        stats={stats}
        activeFilter={statusFilter}
        onFilter={setStatusFilter}
      />

      {/* ── Search & Filters ── */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by title or employee..."
            className="w-full rounded-xl border border-gray-200 bg-gray-50 pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white focus:border-transparent transition"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white transition appearance-none cursor-pointer"
        >
          {STATUSES.map(s => <option key={s} value={s}>{s || 'All Status'}</option>)}
        </select>
        <select
          value={priorityFilter}
          onChange={e => setPriorityFilter(e.target.value)}
          className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white transition appearance-none cursor-pointer"
        >
          {PRIORITIES.map(p => <option key={p} value={p}>{p || 'All Priority'}</option>)}
        </select>
      </div>

      {/* ── Table ── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-8 h-8 border-2 border-[#7AA33E] border-t-transparent rounded-full animate-spin" />
            <p className="text-gray-400 text-sm">Loading subtasks...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
            <svg className="w-12 h-12 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p className="text-sm font-medium text-gray-500">
              {subtasks.length === 0 ? 'No subtasks yet' : 'No subtasks match your filters'}
            </p>
            {canCreate && subtasks.length === 0 && (
              <button
                onClick={() => setShowCreate(true)}
                className="mt-1 text-sm text-[#7AA33E] font-semibold hover:underline"
              >
                + Create the first subtask
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-gray-500 uppercase text-xs tracking-wider">
                  <th className="py-3 px-4 font-semibold">Title</th>
                  <th className="py-3 px-4 font-semibold">Assigned Employee</th>
                  <th className="py-3 px-4 font-semibold">Priority</th>
                  <th className="py-3 px-4 font-semibold">Status</th>
                  <th className="py-3 px-4 font-semibold">Due Date</th>
                  <th className="py-3 px-4 font-semibold">Created By</th>
                  <th className="py-3 px-4 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(s => (
                  <SubtaskCard
                    key={s.id}
                    subtask={s}
                    onOpen={setActiveSubtask}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Dialogs */}
      {showCreate && (
        <CreateSubtaskDialog
          taskId={taskId}
          onCreated={handleCreated}
          onClose={() => setShowCreate(false)}
        />
      )}

      <SubtaskDrawer
        subtask={activeSubtask}
        onClose={() => setActiveSubtask(null)}
        onUpdated={handleUpdated}
        canEdit={effectiveCanEdit}
        currentUser={currentUser}
        onViewParentTask={onViewParentTask}
      />
    </div>
  );
}
